import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Filter,
  X,
  Heart,
  Share2,
  ExternalLink,
  ChefHat,
  Shirt,
  Sofa,
  Bed,
  Sparkles,
  Crown,
  Home,
  Wallet,
  ArrowRight,
  Search,
  Grid3X3,
  LayoutGrid,
  SlidersHorizontal,
  Check,
  Eye,
  MessageCircle,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DesignGallery = () => {
  const [selectedStyle, setSelectedStyle] = useState('all');
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [selectedBudget, setSelectedBudget] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [likedDesigns, setLikedDesigns] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // grid or masonry

  const styles = [
    { id: 'all', label: 'All Styles', icon: Grid3X3 },
    { id: 'modern', label: 'Modern Minimalist', icon: Sparkles },
    { id: 'contemporary', label: 'Contemporary', icon: Home },
    { id: 'traditional', label: 'Traditional Indian', icon: Crown },
    { id: 'luxury', label: 'Luxury Premium', icon: Crown }
  ];

  const rooms = [
    { id: 'all', label: 'All Rooms', icon: Home },
    { id: 'kitchen', label: 'Kitchen', icon: ChefHat },
    { id: 'wardrobe', label: 'Wardrobe', icon: Shirt },
    { id: 'living', label: 'Living Room', icon: Sofa },
    { id: 'bedroom', label: 'Bedroom', icon: Bed }
  ];

  const budgets = [
    { id: 'all', label: 'All Budgets', icon: Wallet },
    { id: 'premium', label: 'Premium', desc: 'Luxury', color: 'text-amber-500' },
    { id: 'mid', label: 'Mid-Range', desc: 'Balanced', color: 'text-blue-500' },
    { id: 'economy', label: 'Economy', desc: 'Value', color: 'text-green-500' }
  ];

  // Design gallery data
  const designs = [
    {
      id: 1,
      title: 'Sleek White Modular Kitchen',
      titleHi: 'स्लीक व्हाइट मॉड्यूलर किचन',
      room: 'kitchen',
      style: 'modern',
      budget: 'premium',
      image: '/gallery-kitchen-modern-white.jpg',
      product: '6H Acrylic Laminated Boards',
      finish: 'High Gloss White',
      description: 'Minimalist design with handleless cabinets, integrated appliances, and clean lines. Perfect for contemporary homes.',
      features: ['Handleless design', 'Soft-close hinges', 'LED under-cabinet lighting', 'Waterfall island'],
    },
    {
      id: 2,
      title: 'Warm Wood-Tone Kitchen',
      titleHi: 'वार्म वुड-टोन किचन',
      room: 'kitchen',
      style: 'contemporary',
      budget: 'mid',
      image: '/gallery-kitchen-wood-tone.jpg',
      product: 'Mica Laminated Boards',
      finish: 'Oak Wood Texture',
      description: 'Warm wooden tones combined with modern functionality. Creates a cozy yet stylish cooking space.',
      features: ['Wood grain finish', 'Brass handles', 'Open shelving', 'Breakfast counter'],
    },
    {
      id: 3,
      title: 'Traditional Pooja Room Kitchen',
      titleHi: 'पारंपरिक पूजा रूम किचन',
      room: 'kitchen',
      style: 'traditional',
      budget: 'mid',
      image: '/gallery-kitchen-traditional.jpg',
      product: 'Mica Laminated Boards',
      finish: 'Cherry Wood',
      description: 'Traditional Indian kitchen with brass accents, carved details, and warm colors. Includes integrated pooja shelf.',
      features: ['Carved panel doors', 'Brass knobs', 'Pooja shelf', 'Spice rack'],
    },
    {
      id: 4,
      title: 'Luxury Black & Gold Kitchen',
      titleHi: 'लग्जरी ब्लैक & गोल्ड किचन',
      room: 'kitchen',
      style: 'luxury',
      budget: 'premium',
      image: '/gallery-kitchen-luxury-black.jpg',
      product: '6H Acrylic Laminated Boards',
      finish: 'High Gloss Black + Gold Accents',
      description: 'Opulent design with black cabinets, gold hardware, and marble countertops. Statement piece for luxury homes.',
      features: ['Gold hardware', 'Marble countertop', 'Wine storage', 'Designer hood'],
    },
    {
      id: 5,
      title: 'Budget-Friendly L-Shape Kitchen',
      titleHi: 'बजट फ्रेंडली L-शेप किचन',
      room: 'kitchen',
      style: 'modern',
      budget: 'economy',
      image: '/gallery-kitchen-budget.jpg',
      product: 'Mica Laminated Boards',
      finish: 'Matte Grey',
      description: 'Smart design maximizing space in compact kitchens. All essential features at an affordable price.',
      features: ['L-shape layout', 'Corner carousel', 'Chimney hood', 'Basic accessories'],
    },
    {
      id: 6,
      title: 'Walk-in Closet Premium',
      titleHi: 'वॉक-इन क्लोज़ेट प्रीमियम',
      room: 'wardrobe',
      style: 'luxury',
      budget: 'premium',
      image: '/gallery-wardrobe-walkin.jpg',
      product: '6H Acrylic Laminated Boards',
      finish: 'High Gloss Champagne',
      description: 'Luxurious walk-in closet with island, LED lighting, and glass-front cabinets. A dressing room experience.',
      features: ['Central island', 'Glass doors', 'Jewelry drawers', 'Full-length mirror'],
    },
    {
      id: 7,
      title: 'Sliding Door Wardrobe Modern',
      titleHi: 'स्लाइडिंग डोर वार्डरोब मॉडर्न',
      room: 'wardrobe',
      style: 'modern',
      budget: 'mid',
      image: '/gallery-wardrobe-sliding.jpg',
      product: 'Mica Laminated Boards',
      finish: 'Matte White + Mirror',
      description: 'Space-saving sliding doors with mirror panel. Perfect for bedrooms with limited space.',
      features: ['Sliding mechanism', 'Mirror panel', 'Internal lighting', 'Adjustable shelves'],
    },
    {
      id: 8,
      title: 'Traditional Carved Wardrobe',
      titleHi: 'पारंपरिक कार्व्ड वार्डरोब',
      room: 'wardrobe',
      style: 'traditional',
      budget: 'mid',
      image: '/gallery-wardrobe-traditional.jpg',
      product: 'Veneer Doors (Nail-Free)',
      finish: 'Teak Veneer',
      description: 'Classic design with carved panels and brass handles. Timeless elegance for traditional homes.',
      features: ['Carved panels', 'Brass handles', 'Cedar lining', 'Antique finish'],
    },
    {
      id: 9,
      title: 'Compact Wardrobe Economy',
      titleHi: 'कॉम्पैक्ट वार्डरोब इकोनॉमी',
      room: 'wardrobe',
      style: 'contemporary',
      budget: 'economy',
      image: '/gallery-wardrobe-compact.jpg',
      product: 'Mica Laminated Boards',
      finish: 'Walnut Texture',
      description: 'Smart storage solution for small bedrooms. Maximizes vertical space efficiently.',
      features: ['Loft storage', 'Hanging rod', 'Drawer unit', 'Open shelves'],
    },
    {
      id: 10,
      title: 'TV Unit with Wall Panels',
      titleHi: 'टीवी यूनिट विथ वॉल पैनल्स',
      room: 'living',
      style: 'modern',
      budget: 'mid',
      image: '/gallery-living-tvunit.jpg',
      product: 'Decorative Wall Panels',
      finish: 'Charcoal Grey + Wood',
      description: 'Entertainment center with acoustic panels and integrated storage. Clean cable management.',
      features: ['Floating design', 'Cable management', 'LED backlight', 'Display shelves'],
    },
    {
      id: 11,
      title: 'Luxury Living Room Paneling',
      titleHi: 'लग्जरी लिविंग रूम पैनलिंग',
      room: 'living',
      style: 'luxury',
      budget: 'premium',
      image: '/gallery-living-luxury.jpg',
      product: '6H Acrylic Wall Panels',
      finish: 'High Gloss Burgundy + Gold',
      description: 'Statement wall with 3D panels and concealed lighting. Creates a dramatic focal point.',
      features: ['3D texture panels', 'Cove lighting', 'Gold inlays', 'Acoustic treatment'],
    },
    {
      id: 12,
      title: 'Traditional Mandir Wall',
      titleHi: 'पारंपरिक मंदिर वॉल',
      room: 'living',
      style: 'traditional',
      budget: 'mid',
      image: '/gallery-living-mandir.jpg',
      product: 'CNC Designer Doors',
      finish: 'Golden Oak + CNC Jali',
      description: 'Beautiful pooja area with CNC-cut jali patterns and integrated lighting.',
      features: ['CNC jali pattern', 'LED dias lighting', 'Storage drawers', 'Bell hanging'],
    },
    {
      id: 13,
      title: 'Master Bedroom Headboard',
      titleHi: 'मास्टर बेडरूम हेडबोर्ड',
      room: 'bedroom',
      style: 'luxury',
      budget: 'premium',
      image: '/gallery-bedroom-headboard.jpg',
      product: '6H Acrylic Laminated Boards',
      finish: 'High Gloss Cream + Leather',
      description: 'Floor-to-ceiling headboard with integrated side tables and ambient lighting.',
      features: ['Upholstered panels', 'Side tables', 'Reading lights', 'USB charging'],
    },
    {
      id: 14,
      title: 'Kids Room Storage Wall',
      titleHi: 'किड्स रूम स्टोरेज वॉल',
      room: 'bedroom',
      style: 'contemporary',
      budget: 'mid',
      image: '/gallery-bedroom-kids.jpg',
      product: 'Mica Laminated Boards',
      finish: 'Pastel Colors Mix',
      description: 'Fun and functional storage for children room. Easy-clean surfaces and rounded edges.',
      features: ['Colorful design', 'Study desk', 'Book shelves', 'Toy storage'],
    },
    {
      id: 15,
      title: 'Guest Bedroom Simple',
      titleHi: 'गेस्ट बेडरूम सिंपल',
      room: 'bedroom',
      style: 'modern',
      budget: 'economy',
      image: '/gallery-bedroom-guest.jpg',
      product: 'Mica Laminated Boards',
      finish: 'Light Grey Matte',
      description: 'Clean and simple design for guest room. All essentials without excess.',
      features: ['Murphy bed option', 'Basic wardrobe', 'Side table', 'Mirror'],
    },
    {
      id: 16,
      title: 'CNC Designer Main Door',
      titleHi: 'CNC डिज़ाइनर मेन डोर',
      room: 'living',
      style: 'contemporary',
      budget: 'premium',
      image: '/gallery-door-main.jpg',
      product: 'CNC Designer Doors',
      finish: 'Rosewood + Brass Inlay',
      description: 'Grand entrance door with intricate CNC patterns and brass accents.',
      features: ['CNC 3D pattern', 'Brass inlay', 'Security lock', 'Peephole'],
    }
  ];

  // Filter designs
  const filteredDesigns = useMemo(() => {
    return designs.filter(design => {
      const matchesStyle = selectedStyle === 'all' || design.style === selectedStyle;
      const matchesRoom = selectedRoom === 'all' || design.room === selectedRoom;
      const matchesBudget = selectedBudget === 'all' || design.budget === selectedBudget;
      return matchesStyle && matchesRoom && matchesBudget;
    });
  }, [selectedStyle, selectedRoom, selectedBudget]);

  const toggleLike = (id) => {
    setLikedDesigns(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const brandedImageUrl = (design) => `${API_URL}/api/gallery-image/${design.image.replace('/', '')}`;

  const downloadDesign = async (design) => {
    try {
      const res = await fetch(brandedImageUrl(design));
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `crilam-${design.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed. Please try again.');
    }
  };

  const shareDesign = async (design) => {
    const shareText = `Check out this ${design.title} design from CriLam Decorative!`;
    try {
      const res = await fetch(brandedImageUrl(design));
      const blob = await res.blob();
      const file = new File([blob], 'crilam-design.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: design.title, text: shareText });
        return;
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
    }
    navigator.clipboard.writeText(`${shareText} ${brandedImageUrl(design)}`);
    alert('Link copied to clipboard!');
  };

  const whatsappDesignUrl = (design) => {
    const message = `Hi CriLam Team,\n\nI love the "${design.title}" design from your Design Gallery:\n- Room: ${rooms.find(r => r.id === design.room)?.label}\n- Style: ${styles.find(s => s.id === design.style)?.label}\n- Recommended Product: ${design.product} (${design.finish})\n\nSee the design: ${brandedImageUrl(design)}\n\nPlease share a quote for this design.`;
    return `https://wa.me/918298297475?text=${encodeURIComponent(message)}`;
  };

  const activeFiltersCount = [selectedStyle, selectedRoom, selectedBudget].filter(f => f !== 'all').length;

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="design-gallery-page">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              <Sparkles className="h-4 w-4" />
              DESIGN INSPIRATION
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Design Gallery
            </h1>
            <p className="text-xl text-[#D4AF37] font-medium mb-4">
              Explore Beautiful Interiors Made with CriLam Products
            </p>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Filter by style, room type, and budget to find the perfect design inspiration for your home
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-4 bg-white border-b sticky top-24 z-30 shadow-sm">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between gap-4">
            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-6 flex-1">
              {/* Style Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500">Style:</span>
                <div className="flex gap-1">
                  {styles.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setSelectedStyle(style.id)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        selectedStyle === style.id
                          ? 'bg-[#004534] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`filter-style-${style.id}`}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Room Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500">Room:</span>
                <div className="flex gap-1">
                  {rooms.map(room => (
                    <button
                      key={room.id}
                      onClick={() => setSelectedRoom(room.id)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        selectedRoom === room.id
                          ? 'bg-[#004534] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`filter-room-${room.id}`}
                    >
                      {room.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500">Budget:</span>
                <div className="flex gap-1">
                  {budgets.map(budget => (
                    <button
                      key={budget.id}
                      onClick={() => setSelectedBudget(budget.id)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        selectedBudget === budget.id
                          ? 'bg-[#004534] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`filter-budget-${budget.id}`}
                    >
                      {budget.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowFilters(true)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="w-5 h-5 bg-[#004534] text-white text-xs rounded-full flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* Results Count & View Toggle */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {filteredDesigns.length} designs
              </span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('masonry')}
                  className={`p-1.5 rounded ${viewMode === 'masonry' ? 'bg-white shadow' : ''}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filter Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 lg:hidden"
            onClick={() => setShowFilters(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="absolute right-0 top-0 h-full w-80 bg-white p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold">Filters</h3>
                <button onClick={() => setShowFilters(false)}>
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Style */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Style</h4>
                <div className="space-y-2">
                  {styles.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setSelectedStyle(style.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg ${
                        selectedStyle === style.id ? 'bg-[#004534] text-white' : 'bg-gray-100'
                      }`}
                    >
                      <span>{style.label}</span>
                      {selectedStyle === style.id && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Room */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Room</h4>
                <div className="space-y-2">
                  {rooms.map(room => (
                    <button
                      key={room.id}
                      onClick={() => setSelectedRoom(room.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg ${
                        selectedRoom === room.id ? 'bg-[#004534] text-white' : 'bg-gray-100'
                      }`}
                    >
                      <span>{room.label}</span>
                      {selectedRoom === room.id && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Budget</h4>
                <div className="space-y-2">
                  {budgets.map(budget => (
                    <button
                      key={budget.id}
                      onClick={() => setSelectedBudget(budget.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg ${
                        selectedBudget === budget.id ? 'bg-[#004534] text-white' : 'bg-gray-100'
                      }`}
                    >
                      <span>{budget.label}</span>
                      {selectedBudget === budget.id && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setShowFilters(false)}
                className="w-full py-3 bg-[#004534] text-white font-medium rounded-lg"
              >
                Apply Filters
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Grid */}
      <section className="py-12">
        <div className="container mx-auto px-6 lg:px-12">
          {filteredDesigns.length === 0 ? (
            <div className="text-center py-20">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">No designs found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your filters</p>
              <button
                onClick={() => {
                  setSelectedStyle('all');
                  setSelectedRoom('all');
                  setSelectedBudget('all');
                }}
                className="px-6 py-2 bg-[#004534] text-white rounded-lg"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}>
              {filteredDesigns.map((design, index) => (
                <motion.div
                  key={design.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                  data-testid={`design-card-${design.id}`}
                  onClick={() => setSelectedDesign(design)}
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img 
                      src={design.image} 
                      alt={design.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '';
                        e.target.parentElement.innerHTML = `<div class="absolute inset-0 bg-gradient-to-br from-[#004534] to-[#0A0D0C] flex items-center justify-center"><div class="text-center text-white/50 p-4"><span class="text-sm">${design.title}</span></div></div>`;
                      }}
                    />
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedDesign(design); }}
                        className="px-4 py-2 bg-white text-[#004534] font-medium rounded-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform"
                        data-testid={`view-details-${design.id}`}
                      >
                        <Eye className="h-4 w-4" />
                        View Details
                      </button>
                    </div>

                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        design.budget === 'premium' ? 'bg-amber-500 text-white' :
                        design.budget === 'mid' ? 'bg-blue-500 text-white' :
                        'bg-green-500 text-white'
                      }`}>
                        {design.budget === 'premium' ? 'Premium' : design.budget === 'mid' ? 'Mid-Range' : 'Economy'}
                      </span>
                    </div>

                    {/* Like Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleLike(design.id); }}
                      className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                      data-testid={`like-btn-${design.id}`}
                    >
                      <Heart className={`h-5 w-5 ${
                        likedDesigns.includes(design.id) 
                          ? 'fill-red-500 text-red-500' 
                          : 'text-gray-600'
                      }`} />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-[#004534] line-clamp-1">{design.title}</h3>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{design.titleHi}</p>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {rooms.find(r => r.id === design.room)?.label}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {styles.find(s => s.id === design.style)?.label}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Product</p>
                        <p className="text-sm font-medium text-[#004534]">{design.product}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Design Detail Modal */}
      <AnimatePresence>
        {selectedDesign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedDesign(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="design-detail-modal"
            >
              {/* Modal Header */}
              <div className="relative aspect-video overflow-hidden">
                <img 
                  src={selectedDesign.image} 
                  alt={selectedDesign.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.parentElement.innerHTML = `<div class="w-full h-full bg-gradient-to-br from-[#004534] to-[#0A0D0C] flex items-center justify-center"><div class="text-center text-white/50 p-4"><span class="text-lg">${selectedDesign.title}</span></div></div>`;
                  }}
                />
                <button
                  onClick={() => setSelectedDesign(null)}
                  className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 lg:p-8">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-[#004534] font-['Outfit']">
                      {selectedDesign.title}
                    </h2>
                    <p className="text-gray-500">{selectedDesign.titleHi}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleLike(selectedDesign.id)}
                      className="p-2 border rounded-lg hover:bg-gray-50"
                    >
                      <Heart className={`h-5 w-5 ${
                        likedDesigns.includes(selectedDesign.id) 
                          ? 'fill-red-500 text-red-500' 
                          : 'text-gray-600'
                      }`} />
                    </button>
                    <button
                      onClick={() => downloadDesign(selectedDesign)}
                      className="p-2 border rounded-lg hover:bg-gray-50"
                      title="Download branded image"
                      data-testid="download-design-btn"
                    >
                      <Download className="h-5 w-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => shareDesign(selectedDesign)}
                      className="p-2 border rounded-lg hover:bg-gray-50"
                      data-testid="share-design-btn"
                    >
                      <Share2 className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                </div>

                <p className="text-gray-600 mb-6">{selectedDesign.description}</p>

                {/* Product Info */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="p-4 bg-[#004534]/5 rounded-xl">
                    <p className="text-sm text-gray-500 mb-1">Recommended Product</p>
                    <p className="font-bold text-[#004534]">{selectedDesign.product}</p>
                    <p className="text-sm text-gray-600">{selectedDesign.finish}</p>
                  </div>
                  <div className="p-4 bg-[#D4AF37]/10 rounded-xl">
                    <p className="text-sm text-gray-500 mb-1">Bonding &amp; Warranty</p>
                    <p className="font-bold text-[#004534] text-xl">CriLam PUR-Bond™ · 10-Year Warranty</p>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h3 className="font-bold text-[#004534] mb-3">Design Features</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedDesign.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                  <a
                    href={whatsappDesignUrl(selectedDesign)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <button className="w-full py-3 bg-[#25D366] text-white font-medium rounded-lg hover:bg-[#128C7E] transition-colors flex items-center justify-center gap-2" data-testid="whatsapp-design-btn">
                      <MessageCircle className="h-5 w-5" />
                      WhatsApp This Design
                    </button>
                  </a>
                  <Link to="/product-recommender" className="flex-1">
                    <button className="w-full py-3 bg-[#004534] text-white font-medium rounded-lg hover:bg-[#D4AF37] hover:text-[#004534] transition-colors flex items-center justify-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      Get Personalized Recommendation
                    </button>
                  </Link>
                  <Link to="/contact" className="flex-1">
                    <button className="w-full py-3 border-2 border-[#004534] text-[#004534] font-medium rounded-lg hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2">
                      Get Quote for This Design
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-16 bg-[#004534]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Outfit']">
              Ready to Create Your Dream Space?
            </h2>
            <p className="text-white/70 mb-8">
              Get personalized recommendations based on your requirements, or visualize products in your actual room.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/product-recommender">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#004534] font-medium rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  AI Product Recommender
                </button>
              </Link>
              <Link to="/ai-visualizer">
                <button className="px-8 py-4 border-2 border-white text-white font-medium rounded-lg hover:bg-white hover:text-[#004534] transition-colors flex items-center gap-2">
                  AI Room Visualizer
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignGallery;
